import { ChangeDetectionStrategy, Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  ValidationErrors,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';

import { RegisterFormStepComponent } from '../../components/register/register-form-step/register-form-step.component';
import { ProgressBarComponent } from '../../components/progress-bar/progress-bar.component';
import { Router } from '@angular/router';
import { RegistrationService } from '../../services/registration/registration.service';
import { ToastrService } from 'ngx-toastr';
import { Tokens } from '../../entities/authentication/tokens.entity';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { User, UserRoles, UserStatus } from '../../entities/user/user.entity';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { MetaPixelService } from '../../services/analytics/meta-pixel.service';
import { isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { LocalStorageService } from '../../services/localstorage/local-storage.service';
import { LanguageService } from '../../services/language/language.service';
import { SubscriptionService } from '../../services/subscription/subscription.service';
import { AuthLayoutComponent } from '../../components/auth-layout/auth-layout.component';

/** Validator to ensure passwords match */
export const passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const password = control.get('password')?.value;
  const confirmPassword = control.get('passwordConfirm')?.value;
  return password === confirmPassword ? null : { passwordMismatch: true };
};

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  imports: [
    TranslocoPipe,
    FormsModule,
    RegisterFormStepComponent,
    ReactiveFormsModule,
    ProgressBarComponent,
    NgOptimizedImage,
    AuthLayoutComponent,
  ],
  styleUrl: './register.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegisterComponent implements OnInit {
  private readonly registrationService = inject(RegistrationService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly fb = inject(FormBuilder);
  private readonly toastr = inject(ToastrService);
  private readonly router = inject(Router);
  private readonly analytics = inject(AnalyticsService);
  private readonly metaPixel = inject(MetaPixelService);
  private readonly localStorage = inject(LocalStorageService);
  private readonly languageService = inject(LanguageService);
  private readonly platformId = inject(PLATFORM_ID);

  public currentStep = 1;
  public submitting = false;
  public static readonly localStorageKey = 'registrationForm';
  public registerForm: FormGroup = this.fb.group(
    {
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      passwordConfirm: ['', [Validators.required, Validators.minLength(6)]],
      selectedPlan: ['', Validators.required],
      role: [UserRoles.photographer, Validators.required],
    },
    { validators: passwordMatchValidator }
  );
  public currentUser: User|null = null;
  public nextLoading = false;
  public readonly comparisonRows = [
    { key: 'pricing.advantages.public_page', free: true, pro: true },
    { key: 'pricing.advantages.online_booking', free: true, pro: true },
    { key: 'pricing.advantages.stripe_payment', free: true, pro: true },
    { key: 'pricing.advantages.auto_invoices', free: true, pro: true },
    { key: 'pricing.advantages.gallery_delivery', free: true, pro: true },
    { key: 'pricing.advantages.photo_storage_30d', free: true, pro: true },
    { key: 'pricing.advantages.photo_storage_365d', free: false, pro: true },
    { key: 'pricing.advantages.services_limit_free', free: true, pro: true },
    { key: 'pricing.advantages.services_unlimited', free: false, pro: true },
    { key: 'pricing.advantages.unlimited_clients_bookings', free: true, pro: true },
    { key: 'pricing.advantages.web_mobile_access', free: true, pro: true },
    { key: 'pricing.advantages.stats', free: false, pro: true },
    { key: 'pricing.advantages.custom_domain', free: false, pro: true },
    { key: 'pricing.advantages.marketplace_featured', free: false, pro: true },
    { key: 'pricing.advantages.verified_badge', free: false, pro: true },
    { key: 'pricing.advantages.priority_support', free: false, pro: true },
  ];
  public readonly comparisonIcons: Record<string, string> = {
    'pricing.advantages.public_page': 'fa-pager',
    'pricing.advantages.online_booking': 'fa-calendar-check',
    'pricing.advantages.stripe_payment': 'fa-credit-card',
    'pricing.advantages.auto_invoices': 'fa-file-invoice',
    'pricing.advantages.gallery_delivery': 'fa-images',
    'pricing.advantages.photo_storage_30d': 'fa-archive',
    'pricing.advantages.photo_storage_365d': 'fa-archive',
    'pricing.advantages.services_limit_free': 'fa-list-check',
    'pricing.advantages.services_unlimited': 'fa-list-check',
    'pricing.advantages.unlimited_clients_bookings': 'fa-users',
    'pricing.advantages.web_mobile_access': 'fa-mobile-alt',
    'pricing.advantages.stats': 'fa-chart-line',
    'pricing.advantages.custom_domain': 'fa-link',
    'pricing.advantages.marketplace_featured': 'fa-star',
    'pricing.advantages.verified_badge': 'fa-certificate',
    'pricing.advantages.priority_support': 'fa-headset',
  };
  public readonly socialAvatars = [
    { src: 'assets/images/landing/antoine.jpg', alt: 'Antoine is on ShutterFlow' },
    { src: 'assets/images/landing/manon.jpg', alt: 'Manon is on ShutterFlow' },
    { src: 'assets/images/landing/adrien.jpg', alt: 'Adrien is on ShutterFlow' },
    { src: 'assets/images/landing/gael.jpg', alt: 'Gael is on ShutterFlow' },
  ];

  constructor() {}

  ngOnInit(): void {
    this.loadFormFromLocalStorage();
    this.determineCurrentStep();
  }

  /** Getters for form values */
  private get firstname(): string {
    return this.registerForm.get('firstname')?.value;
  }
  private get lastname(): string {
    return this.registerForm.get('lastname')?.value;
  }
  private get email(): string {
    return this.registerForm.get('email')?.value;
  }
  private get password(): string {
    return this.registerForm.get('password')?.value;
  }
  public get role(): string {
    return this.registerForm.get('role')?.value;
  }

  private get plan(): string {
    return this.registerForm.get('selectedPlan')?.value;
  }

  /** Saves non-sensitive form data to localStorage */
  private saveFormToLocalStorage(): void {
    const { password, passwordConfirm, ...safeData } = this.registerForm.value;
    this.localStorage.save(RegisterComponent.localStorageKey, safeData);
  }

  /** Loads form data from localStorage */
  private loadFormFromLocalStorage(): void {
    const savedForm = this.localStorage.get(RegisterComponent.localStorageKey);
    if (savedForm) {
      this.registerForm.patchValue(savedForm);
    }
  }

  /** Clears localStorage */
  private clearFormFromLocalStorage(): void {
    this.localStorage.remove(RegisterComponent.localStorageKey);
  }

  /** Determines which step the user should start from */
  private determineCurrentStep(): void {
    this.currentUser = this.authenticationService.getCurrentUser();
    if (this.currentUser !== null) {
      if (this.currentUser.isActive) {
        this.router.navigate(['/manage/dashboard']);
        return;
      }
      if (this.currentUser.roles.includes(UserRoles.photographer)) {
        this.registerForm.patchValue({ role: UserRoles.photographer, selectedPlan: 'pro' }, { emitEvent: false });
        this.currentStep = 4;
        return;
      }
      if (this.currentUser.status !== UserStatus.registered) {
        this.router.navigate(['/register/success']);
        return;
      }
      this.router.navigate(['/register/success']);
      return;
    }

    if (!this.firstname || !this.lastname) {
      this.currentStep = 1;
      return;
    }

    if (!this.role) {
      this.currentStep = 2;
      return;
    }

    if (!this.email || !this.password) {
      this.currentStep = 3;
      return;
    }
  }

  /** Advances to the next step */
  public nextStep(): void {
    if (this.currentStep === 3 && this.registerForm.hasError('passwordMismatch')) {
      return; // Prevent progression if passwords don't match
    }

    if (this.currentStep === 1 || this.currentStep === 2) {
      this.currentStep++;
      this.saveFormToLocalStorage();
      return;
    }

    this.analytics.event('onboarding_step_' + this.currentStep);

    // Get the local timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (this.currentStep === 3) {
      if (this.authenticationService.getCurrentUser() !== null) {
        if (this.role === UserRoles.customer) {
          this.clearFormFromLocalStorage();
          this.router.navigate(['/']);
          return;
        }
        this.router.navigate(['/register/success']);
        return;
      }
      this.submitting = true;
      this.nextLoading = true;
      const preferredLanguage =
        this.languageService.getPersistedPreferredLanguage() ?? this.languageService.getWebsiteLanguage();
      this.registrationService.register(
        this.email,
        this.password,
        this.role,
        timezone,
        this.firstname,
        this.lastname,
        preferredLanguage
      ).subscribe({
          next: (tokens: Tokens) => {
            this.authenticationService.saveTokens(tokens);
            this.currentUser = this.authenticationService.getCurrentUser();
            this.analytics.event('onboarding_account_created');
            this.metaPixel.trackRegistration({
              user: this.currentUser,
              email: this.email,
              role: this.role,
              plan: this.plan,
              source: 'onboarding',
            });

            this.clearFormFromLocalStorage();
            if (this.role === UserRoles.photographer) {
              this.registerForm.get('selectedPlan')?.setValue('pro');
              this.currentStep = 4;
            } else {
              this.router.navigate(['/']);
            }
            return;
          },
          error: () => {
            this.toastr.error('Please try again. If it fails again, contact support', 'An error occurred');
            this.nextLoading = false;
          },
          complete: () => {
            this.submitting = false;
            this.nextLoading = false;
          }
        });
    }
  }

  public subscribe(): void {
    if (!this.currentUser) {
      this.toastr.error('Please try again. If it fails again, contact support', 'An error occurred');
      return;
    }

    const plan = this.plan || 'pro';
    if (!this.plan) {
      this.selectPlan(plan);
    }

    this.analytics.event('onboarding_subscription_started', { plan });

    this.nextLoading = true;

    this.subscriptionService.subscribe(plan).subscribe(
      (result) => {
        if (!isPlatformBrowser(this.platformId)) {
          this.nextLoading = false;
          return;
        }
        window.location.href = result.url;
        this.nextLoading = false;
      }, () => {
        this.toastr.error('Please try again. If it fails again, contact support', 'An error occurred');
        this.nextLoading = false;
      }
    );
  }

  public skipSubscription(): void {
    this.router.navigate(['/register/success']);
  }

  /** Goes back a step */
  public prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;

      if (this.currentStep === 3) {
        // Set a fake password visually
        this.registerForm.get('password')?.setValue('test1234');
        this.registerForm.get('passwordConfirm')?.setValue('test1234');
      }
    }
  }

  public canGoBack(): boolean {
    return this.currentStep > 1 && this.currentUser === null;
  }

  public progressValue(): number {
    const totalSteps = this.role === UserRoles.photographer ? 4 : 3;
    const maxStepIndex = Math.max(totalSteps - 1, 1);
    return ((this.currentStep - 1) / maxStepIndex) * 100;
  }

  /** Updates the selected plan */
  public selectPlan(plan: string): void {
    this.registerForm.get('selectedPlan')?.setValue(plan);
  }

  public selectRole(role: string): void {
    this.registerForm.get('role')?.setValue(role);
    this.nextStep();
  }

  protected readonly UserRoles = UserRoles;
}
